import { Icon } from '@clarion/ClarionIcon';
import { RefObject } from 'react';
import {
  composeRenderProps,
  Button as RACButton,
  ButtonProps as RACButtonProps,
  TooltipTrigger,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { cx } from '../util';
import { ClarionSpinner } from './/ClarionSpinner';
import { type IconProps } from './ClarionIcon';
import { ClarionTooltip } from './ClarionTooltip';
import { focusRing } from './utils';

export type ClarionButtonOrLinkProps = {
  tooltip?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  variant?: 'minimal' | 'outlined' | 'solid' | 'selected';
  intent?: 'none' | 'primary' | 'success' | 'danger';
  size?: 'small' | 'medium' | 'large';
  icon?: IconProps['icon'];
  endIcon?: IconProps['icon'];
  loading?: boolean;
  fill?: boolean;
  textAlign?: 'left' | 'center' | 'right';
};

export interface ClarionButtonProps extends ClarionButtonOrLinkProps, RACButtonProps {
  ref?: RefObject<HTMLButtonElement>;
  onClick?: (e: any) => void;
}

export const buttonStyles = tv({
  extend: focusRing,
  base: `relative inline-flex items-center justify-center gap-2 border border-transparent dark:border-white/10 min-h-7.5 box-border py-0 text-sm text-center transition rounded-lg cursor-pointer [-webkit-tap-highlight-color:transparent] forced-colors:bg-[ButtonFace] forced-colors:border-[solid_ButtonBorder_1px]! [&:has(>.clarion-icon:only-child)]:justify-center [&:has(>.clarion-icon:only-child)]:aspect-square`,
  variants: {
    variant: {
      solid:
        'bg-gray-100 border-black/10 hover:bg-gray-200 pressed:bg-gray-300 dark:bg-gray-700 dark:border-white/10 dark:hover:bg-gray-800 dark:pressed:bg-gray-900 dark:text-white shadow-sm',
      outlined:
        'border-black/20 bg-transparent hover:bg-neutral-100 pressed:bg-neutral-200 text-neutral-800 dark:border-white/20 dark:bg-transparent dark:hover:bg-neutral-600/70 dark:pressed:bg-neutral-500/70 dark:text-neutral-100',
      selected:
        'border-0 bg-purple-100/50! dark:bg-purple-100/10! dark:text-purple-100! forced-colors:bg-[Highlight]! hover:bg-purple-100/60! dark:hover:bg-purple-400/30!',
      minimal:
        'border-0 bg-transparent hover:bg-neutral-200/70 pressed:bg-neutral-300/70 text-neutral-800 dark:hover:bg-neutral-700/50 dark:pressed:bg-neutral-600/70 dark:text-neutral-100',
    },
    size: {
      small: `min-h-6 px-2 [&:has(>.clarion-icon:only-child)]:w-6`,
      medium: `min-h-8 px-3 [&:has(>.clarion-icon:only-child)]:w-8`,
      large: `min-h-10 text-base gap-3 px-4 [&:has(>.clarion-icon:only-child)]:w-10`,
    },
    textAlign: {
      left: 'text-left justify-start',
      center: 'text-center justify-center',
      right: 'text-right justify-end',
    },
    fill: {
      true: 'w-full',
    },
    isDisabled: {
      true: 'cursor-not-allowed shadow-none border-transparent opacity-50 dark:border-transparent bg-neutral-100 dark:bg-neutral-800 forced-colors:text-[GrayText]',
    },
    isPending: {
      true: 'cursor-not-allowed shadow-none forced-colors:text-[GrayText]',
    },
  },
  defaultVariants: {
    variant: 'solid',
    intent: 'none',
    size: 'medium',
    textAlign: 'center',
  },
  compoundVariants: [
    {
      variant: 'solid',
      intent: 'primary',
      class:
        'bg-purple-600 hover:bg-purple-700 pressed:bg-purple-800 text-white dark:bg-purple-600 dark:hover:bg-purple-700 dark:pressed:bg-purple-800',
    },
    {
      variant: 'solid',
      intent: 'success',
      class:
        'bg-green-600 hover:bg-green-700 pressed:bg-green-800 text-white dark:bg-green-600 dark:hover:bg-green-700 dark:pressed:bg-green-800',
    },
    {
      variant: 'solid',
      intent: 'danger',
      class:
        'bg-red-600 hover:bg-red-700 pressed:bg-red-800 text-white dark:bg-red-600 dark:hover:bg-red-700 dark:pressed:bg-red-800',
    },
    {
      variant: ['outlined', 'minimal'],
      intent: 'primary',
      class:
        'border-purple-600/30 hover:bg-purple-100/50 pressed:bg-purple-100/80 text-purple-800 dark:border-purple-300/70 dark:hover:bg-purple-600/30 dark:pressed:bg-purple-500/50 dark:text-purple-100',
    },
    {
      variant: ['outlined', 'minimal'],
      intent: 'success',
      class:
        'border-green-600/30 hover:bg-green-100/50 pressed:bg-green-100/80 text-green-700 dark:border-green-300/70 dark:hover:bg-green-600/30 dark:pressed:bg-green-500/50 dark:text-green-100',
    },
    {
      variant: ['outlined', 'minimal'],
      intent: 'danger',
      class:
        'border-red-600/30 hover:bg-red-100/50 pressed:bg-red-100/80 text-red-800 dark:border-red-300/70 dark:hover:bg-red-600/30 dark:pressed:bg-red-500/50 dark:text-red-100',
    },
    {
      variant: 'minimal',
      intent: ['primary', 'success', 'danger'],
      class: 'border-0 dark:border-0',
    },
    {
      variant: 'selected',
      intent: 'primary',
      class:
        'bg-purple-100/50! dark:bg-purple-100/10! dark:text-purple-100! forced-colors:bg-[Highlight]! hover:bg-purple-100/60! dark:hover:bg-purple-400/30!',
    },
  ],
});

export const ClarionButton = ({
  ref,
  icon,
  endIcon,
  children,
  tooltip,
  tooltipPlacement = 'bottom',
  onClick,
  loading,
  ...props
}: ClarionButtonProps) => {
  // Make icon color somewhat muted compared to button text color (for no intent)
  const iconClassName =
    !props.intent || props.intent === 'none' ? 'text-gray-600 dark:text-gray-300' : undefined;

  const button = (
    <RACButton
      ref={ref}
      // If tooltip is provided, use it as the aria-label (will be overridden by explicit aria-label in the ...props spread below)
      aria-label={tooltip}
      onPress={onClick}
      isPending={loading}
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        buttonStyles({ ...renderProps, ...props, className }),
      )}
    >
      {composeRenderProps(children, (children) => (
        <>
          {loading ? (
            <ClarionSpinner size={16} />
          ) : (
            icon && <Icon icon={icon} className={iconClassName} />
          )}
          {children && (
            <span
              className={cx(
                'min-w-0',
                (props.textAlign === 'left' || props.textAlign === 'right') && 'grow',
              )}
            >
              {children}
            </span>
          )}
          {endIcon && <Icon icon={endIcon} className={iconClassName} />}
        </>
      ))}
    </RACButton>
  );

  if (tooltip) {
    return (
      <TooltipTrigger delay={500}>
        {button}
        <ClarionTooltip placement={tooltipPlacement}>{tooltip}</ClarionTooltip>
      </TooltipTrigger>
    );
  }

  return button;
};
